/**
 * Dashboard primitives — shared building blocks used by both
 * KoredorCommandDashboard (Strategic lens) and any cockpit-skinned
 * dashboard surface (e.g. OperationsCommandCenter Cockpit branch).
 *
 * Extracted to keep the two lenses visually consistent and to stop the
 * component drift the audit identified. These primitives are CSS-only —
 * no business logic, no data fetching. Safe to consume from anywhere.
 *
 * Classic skin surfaces should NOT import from here — Classic stays on
 * its current shadcn-default look.
 */
import { cn } from "@/lib/utils";

/* ────────────────────────────────────────────────────────────────
 * SectionHeader — bold title left, quiet meta or quiet link right.
 * Gives the cockpit surfaces their consistent rhythm.
 * ──────────────────────────────────────────────────────────────── */
export function SectionHeader({
  title,
  eyebrow,
  actionLabel,
  onAction,
}: {
  title: string;
  eyebrow?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="flex items-baseline justify-between mb-3">
      <h2 className="text-[17px] font-semibold tracking-[-0.014em] text-foreground">{title}</h2>
      {actionLabel && onAction ? (
        <button
          onClick={onAction}
          className="text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        >
          {actionLabel}
        </button>
      ) : eyebrow ? (
        <span className="text-[13px] text-muted-foreground tabular-nums">{eyebrow}</span>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * StatStrip — hairline-divided KPI row. Replaces tinted card grids.
 * Use at the top of a surface, immediately under the header.
 * ──────────────────────────────────────────────────────────────── */
export interface StatItem {
  label: string;
  value: string | number;
  sub?: string;
  accent?: "warn" | "bad" | "good";
}

export function StatStrip({ items }: { items: StatItem[] }) {
  // Auto-fit columns: 2 / 3 / N (capped at 8 so very wide strips remain legible).
  const cols = Math.min(items.length, 8);
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 border-y border-border tabular-nums divide-x divide-border"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` } as React.CSSProperties}
    >
      {items.map((it) => (
        <div key={it.label} className="px-5 py-4 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-medium truncate">
            {it.label}
          </div>
          <div className="mt-1.5 flex items-baseline gap-2">
            <span
              className={cn(
                "text-[22px] font-semibold tracking-[-0.018em] leading-none",
                it.accent === "warn"
                  ? "text-[hsl(var(--warning))]"
                  : it.accent === "bad"
                    ? "text-destructive"
                    : it.accent === "good"
                      ? "text-[hsl(var(--success))]"
                      : "text-foreground",
              )}
            >
              {it.value}
            </span>
            {it.sub && <span className="text-[12px] text-muted-foreground truncate">{it.sub}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * CockpitHeader — page title + subtitle + right-side actions slot.
 * Matches the type scale used across cockpit-skinned surfaces.
 * ──────────────────────────────────────────────────────────────── */
export function CockpitHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <header className="flex items-end justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <h1 className="text-[26px] font-semibold tracking-[-0.022em] leading-tight text-foreground">
          {title}
        </h1>
        {subtitle && (
          <p className="text-[13px] text-muted-foreground mt-1">{subtitle}</p>
        )}
      </div>
      {right && <div className="flex items-center gap-2">{right}</div>}
    </header>
  );
}
